import { useQuery } from "@apollo/client";
import { Alert, Button, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useEffect, useRef, useState } from "react";

import {
  GetAudienceContactFieldsQuery,
  GetAudienceSearchUserFilterQuery,
  GetAudienceSearchUserFilterQueryVariables,
  GetSubaccountShortcodeQuery,
  GetSubaccountShortcodeQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { ButtonLink, LinkButton } from "~frontend/components/Link";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import {
  GET_AUDIENCE_CONTACT_FIELDS,
  GET_AUDIENCE_SEARCH_USER_FILTER,
} from "~frontend/graphql/audience";
import { GET_SUBACCOUNT_SHORTCODE } from "~frontend/graphql/shortcode";
import { useFlagRedirect } from "~frontend/hooks/useFlagRedirect";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { jobFailed, jobInProgress } from "~frontend/util/jobs";

import AddSubscriberDialog from "../shortcode/Subscribers/AddSubscriberDialog";
import JobDetailsDialog from "../shortcode/Subscribers/JobDetailsDialog";
import { AudienceSubscribersTable } from "./AudienceSubscriberTable";
import { AudienceSummary } from "./AudienceSummary";
import { useAudience } from "./store";

function AudiencePageHeader({
  shortcode,
  audienceContactFields,
}: {
  shortcode: NonNullable<
    GetSubaccountShortcodeQuery["getSubaccount"]["shortcode"]
  >;
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
}): ReactElement {
  const isStaff = useCurrentUserIsStaff();
  const { subaccountLink } = useSubaccount()!;
  const [addSubscriberDialogOpen, setAddSubscriberDialogOpen] = useState(false);
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const refetchSearchResults = useAudience(store => store.refetchSearchResults);

  function renderImportJob() {
    const importJob = shortcode.subscriberImportJob;
    if (!importJob) {
      return null;
    }

    let message: string;
    if (jobInProgress(importJob)) {
      message = `Upload in progress...`;
    } else if (jobFailed(importJob).failed) {
      message = `Upload Failed: ${formatShortDateTime(DateTime.fromMillis(importJob.startedAt))}`;
    } else {
      message = `Last Upload: ${formatShortDateTime(DateTime.fromMillis(importJob.startedAt))}`;
    }
    return (
      <Typography variant="body3">
        {message}{" "}
        <LinkButton onClick={() => setJobDetailsOpen(true)}>
          View Details
        </LinkButton>
      </Typography>
    );
  }

  return (
    <>
      <FlexBox gap={4} justifyContent="space-between" alignItems="center">
        <FlexBox gap={2} alignItems="center">
          <AudienceSummary shortcode={shortcode} />
          {renderImportJob()}
        </FlexBox>
        {shortcode.state === "NEW" && isStaff && (
          <Alert severity="warning">This short code is NOT active.</Alert>
        )}
        {shortcode.subscriberAudienceId && (
          <FlexBox gap={1}>
            <ButtonLink
              sx={{
                fontSize: "10px",
                letterSpacing: "0.4px",
                px: 1,
                py: 0.75,
                height: "30px",
              }}
              to={subaccountLink(
                `/audiences/${shortcode.subscriberAudienceId}/settings`
              )}
              variant="contained"
              color="tertiary"
            >
              Audience Settings
            </ButtonLink>
            <Button
              sx={{
                fontSize: "10px",
                letterSpacing: "0.4px",
                px: 1,
                py: 0.75,
                height: "30px",
              }}
              variant="contained"
              color="tertiary"
              onClick={() => setAddSubscriberDialogOpen(true)}
            >
              Add Contacts
            </Button>
          </FlexBox>
        )}
      </FlexBox>
      {addSubscriberDialogOpen && (
        <AddSubscriberDialog
          onClose={() => setAddSubscriberDialogOpen(false)}
          shortcodeId={shortcode.id}
          refreshList={async () => {
            if (refetchSearchResults) {
              await refetchSearchResults();
            }
          }}
          openJobDetails={() => setJobDetailsOpen(true)}
          audienceContactFields={audienceContactFields}
        />
      )}
      {jobDetailsOpen && shortcode.subscriberImportJob && (
        <JobDetailsDialog
          job={shortcode.subscriberImportJob}
          onClose={() => setJobDetailsOpen(false)}
        />
      )}
    </>
  );
}

function AudiencePageContent({
  shortcode,
}: {
  shortcode: NonNullable<
    GetSubaccountShortcodeQuery["getSubaccount"]["shortcode"]
  >;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const {
    loading,
    error,
    data: userSearchFilterData,
  } = useQuery<
    GetAudienceSearchUserFilterQuery,
    GetAudienceSearchUserFilterQueryVariables
  >(GET_AUDIENCE_SEARCH_USER_FILTER, {
    variables: {
      input: {
        subaccountId: subaccount.id,
        audienceId: shortcode.subscriberAudienceId!,
      },
    },
  });

  const { data: audienceFields } = useQuery<GetAudienceContactFieldsQuery>(
    GET_AUDIENCE_CONTACT_FIELDS,
    {
      variables: {
        input: { opnId: shortcode.id, subaccountId: subaccount.id },
      },
    }
  );

  return (
    <FlexBox flexDirection="column" gap={2} sx={{ width: "100%" }}>
      <AudiencePageHeader
        shortcode={shortcode}
        audienceContactFields={audienceFields?.getAudienceContactFields || []}
      />
      <AsyncWrapper
        loading={loading}
        errors={error}
        data={userSearchFilterData}
      >
        {({ getAudienceSearchUserFilter }) => (
          <AudienceSubscribersTable
            shortcode={shortcode}
            userSearchFilter={getAudienceSearchUserFilter}
            fieldDefinitions={audienceFields?.getAudienceContactFields || []}
          />
        )}
      </AsyncWrapper>
    </FlexBox>
  );
}

export function AudiencesPage(): ReactElement {
  useFlagRedirect("useAudiencesV2Page", "/shortcode");

  const { subaccount, subaccountLink } = useSubaccount()!;

  useBreadcrumbs([
    {
      label: "Subscribers",
      path: subaccountLink("/audiences"),
    },
  ]);

  const refetchSearchResults = useAudience(store => store.refetchSearchResults);

  // temporary: once we have generic audiences, we will need to provide a list of all audiences this subaccount has
  // right now, we can assume there is only one audience per subaccount
  const {
    data: shortcodeData,
    loading,
    error,
    startPolling,
    stopPolling,
  } = useQuery<
    GetSubaccountShortcodeQuery,
    GetSubaccountShortcodeQueryVariables
  >(GET_SUBACCOUNT_SHORTCODE, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const jobWasRunning = useRef(false);
  useEffect(() => {
    const importJob =
      shortcodeData?.getSubaccount?.shortcode?.subscriberImportJob;

    if (!importJob) {
      stopPolling();
      return;
    }

    const isRunning = jobInProgress(importJob);
    if (isRunning) {
      startPolling(1000);
    } else {
      stopPolling();
      if (jobWasRunning.current) {
        refetchSearchResults().catch(console.error);
      }
    }

    jobWasRunning.current = isRunning;

    return () => {
      stopPolling();
    };
  }, [
    shortcodeData?.getSubaccount?.shortcode?.subscriberImportJob,
    startPolling,
    stopPolling,
    refetchSearchResults,
  ]);

  return (
    <PageContainer>
      <PageHeader>Subscribers</PageHeader>
      <AsyncWrapper data={shortcodeData} loading={loading} errors={error}>
        {({ getSubaccount }) => {
          const shortcode = getSubaccount.shortcode;
          return (
            <FlexBox justifyContent="space-between" alignItems="center">
              {shortcode ? (
                <AudiencePageContent shortcode={shortcode} />
              ) : (
                <div />
              )}
            </FlexBox>
          );
        }}
      </AsyncWrapper>
    </PageContainer>
  );
}
